import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { Bell, X, Package, Truck, CheckCircle, AlertCircle, Clock, ArrowLeft, ExternalLink } from 'lucide-react';
import './NotificationsPage.css';

function NotificationsPage({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    // Listen for real-time notifications
    const notificationsQuery = doc(db, 'users', user.uid, 'notifications', 'updates');
    const unsubscribe = onSnapshot(notificationsQuery, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const notificationList = data.notifications || [];
        setNotifications(notificationList);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  const markAsRead = async (notificationId) => {
    if (!user) return;

    try {
      const notificationsRef = doc(db, 'users', user.uid, 'notifications', 'updates');
      const updatedNotifications = notifications.map(n => 
        n.id === notificationId ? { ...n, read: true, readAt: serverTimestamp() } : n
      );
      
      await updateDoc(notificationsRef, {
        notifications: updatedNotifications,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      const notificationsRef = doc(db, 'users', user.uid, 'notifications', 'updates');
      const updatedNotifications = notifications.map(n => ({
        ...n,
        read: true,
        readAt: serverTimestamp()
      }));
      
      await updateDoc(notificationsRef, {
        notifications: updatedNotifications,
        lastUpdated: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_confirmed':
        return <CheckCircle className="notification-icon confirmed" />;
      case 'order_processing':
        return <Package className="notification-icon processing" />;
      case 'order_shipped':
        return <Truck className="notification-icon shipped" />;
      case 'order_delivered':
        return <CheckCircle className="notification-icon delivered" />;
      case 'order_cancelled':
        return <AlertCircle className="notification-icon cancelled" />;
      default:
        return <Clock className="notification-icon default" />;
    }
  };

  const getNotificationTitle = (type) => {
    switch (type) {
      case 'order_confirmed':
        return 'Order Confirmed';
      case 'order_processing':
        return 'Order Processing';
      case 'order_shipped':
        return 'Order Shipped';
      case 'order_delivered':
        return 'Order Delivered';
      case 'order_cancelled':
        return 'Order Cancelled';
      default:
        return 'Order Update';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    
    const now = new Date();
    const time = timestamp.toDate();
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return time.toLocaleDateString();
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    // Navigate to order tracking if it has an orderId
    if (notification.orderId) {
      window.location.href = `/track/${notification.orderId}`;
    }
  };

  const goBack = () => {
    window.history.back();
  };

  if (!user) return null;

  return (
    <div className="notifications-page">
      <div className="notifications-header">
        <div className="header-left">
          <button onClick={goBack} className="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div className="header-title">
            <h1>Notifications</h1>
            <p>Your order updates and alerts</p>
          </div>
        </div>
        {notifications.filter(n => !n.read).length > 0 && (
          <button onClick={markAllAsRead} className="mark-all-read-btn">
            Mark all as read
          </button>
        )}
      </div>

      <div className="notifications-content">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length > 0 ? (
          <div className="notifications-list">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-card ${!notification.read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="notification-icon-wrapper">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="notification-details">
                  <div className="notification-header-info">
                    <h3>{getNotificationTitle(notification.type)}</h3>
                    <span className="notification-time">
                      {formatTime(notification.timestamp)}
                    </span>
                  </div>
                  <p className="notification-message">{notification.message}</p>
                  {notification.orderId && (
                    <div className="notification-order">
                      <span>Order #{notification.orderId.slice(-6).toUpperCase()}</span>
                      <ExternalLink size={14} />
                    </div>
                  )}
                </div>
                {!notification.read && (
                  <div className="unread-indicator"></div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Bell size={48} />
            <h3>No notifications</h3>
            <p>You're all caught up! Check back here for order updates.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default NotificationsPage;
